import React, { memo, FC } from 'react'
import classNames from 'classnames'
import { times } from 'lodash'
import Skeleton from 'transfix-ui/core/Skeleton'
import { makeStyles } from 'transfix-ui/core/styles'
import { CETask, ITaskShipment } from 'types'
import { Shipment } from 'apps/carrier/apiTypes/activeShipments'
import { useGetShipmentIssues } from 'apps/marketplace/apiTypes/shipmentIssues'
import { useIssuePriorityOptions } from 'apps/marketplace/pages/issues/shared/priorityOptions'
import { Tag, TAG_SIZE, TAG_STYLE, ContextCard } from 'transfix-ui'
import { DISPLAY } from 'constants/display'
import { IssueStatus } from 'constants/issueStatus'
import { ISSUE_TAG_PROPS } from './constants/issuesStatus'

const useStyles = makeStyles({
  container: {
    minWidth: 400,
  },
  tagContainer: {
    marginBottom: 5,
    '& > *': {
      marginRight: '.5rem',
    },
  },
  openIssueContainer: {
    textAlign: 'center',
  },
  contextCard: {
    width: '100%',
    height: '100%',
    display: 'block',
  },
})

export interface OpenIssuesProps {
  shipment: Shipment | CETask | ITaskShipment
}

const OpenIssuesContent: FC<OpenIssuesProps> = memo(({ shipment }) => {
  const { id: shipmentId = 0, openShipmentIssuesCount = 0 } = shipment || {}
  const { data, loading } = useGetShipmentIssues({
    shipmentId,
    queryParams: { filters: { open_issues: true } },
  })
  const { shipmentIssues = [] } = data || {}
  const classes = useStyles()
  const priorityOptions = useIssuePriorityOptions()

  return (
    <div className={classes.container}>
      {loading
        ? times(openShipmentIssuesCount, String).map(key => (
            <Skeleton key={key} width={400} height={29} />
          ))
        : shipmentIssues.map(
            ({ id, priority, shipmentIssueOwner, shipmentIssueType, state = '' }) => {
              const { style = TAG_STYLE.warning, outline = false, label = '' } =
                ISSUE_TAG_PROPS[state as IssueStatus] || {}
              const { className } = priorityOptions.find(({ value }) => value === priority) || {}
              const priorityClasses = classNames(className, classes.tagContainer)
              return (
                <div className={priorityClasses} key={id}>
                  <a href={`${__ROOT__}/shipment-details/${shipment.id}?issueId=${id}`}>
                    {shipment.id}-{id}: {shipmentIssueType.name}
                  </a>
                  <Tag
                    size={TAG_SIZE.xsmall}
                    label={label}
                    tagStyle={style}
                    outlineOnly={outline}
                  />
                  <span className='text-secondary'>
                    {shipmentIssueOwner?.firstName} {shipmentIssueOwner?.lastName}
                  </span>
                </div>
              )
            }
          )}
    </div>
  )
})

/**
 * Use `<OpenIssuesCard />` for gql IShipmentIssues
 */
export const OpenIssues: FC<OpenIssuesProps> = memo(({ shipment }) => {
  const { openShipmentIssuesCount = 0 } = shipment
  const isGreaterThanZero = openShipmentIssuesCount > 0
  const classes = useStyles()
  return (
    <div className={classes.openIssueContainer}>
      {isGreaterThanZero ? (
        <ContextCard
          className={classes.contextCard}
          title='Open Issues'
          triggerText={`${shipment.openShipmentIssuesCount}`}>
          <OpenIssuesContent shipment={shipment} />
        </ContextCard>
      ) : (
        <p className='text-secondary'>{DISPLAY.empty}</p>
      )}
    </div>
  )
})
